import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Tag
} from '@arco-design/web-react';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

import { get, post } from '../requestNew';
import PhoneCell from '../components/PhoneCell';

import './index.scss';

type CardType = 1 | 2 | 3 | 4;

type UmsUserMember = {
  id: number;
  openId: string;
  oneId?: string;
  gameName: string;
  phone: string;
  orderId: string;
  cardNo: string;
  cardType: CardType;
  cardStatus: number;
  openTime: string;
  expireTime: string;
  isValid: number;
  remark?: string;
  createTime?: string;
  updateTime?: string;
};

type UserOption = {
  label: string; // gameName
  value: string; // openId/openId
  phone?: string;
};

const Option = Select.Option;
const RangePicker = DatePicker.RangePicker;

// 卡券类型标签配色（参照设计稿：填充色 + 白字）
const cardTypeMap: Record<CardType, { label: string; color: string; bgColor: string }> = {
  1: { label: '周卡', color: '#FFFFFF', bgColor: '#00B42A' },
  2: { label: '月卡', color: '#FFFFFF', bgColor: '#FFB020' },
  3: { label: '季卡', color: '#FFFFFF', bgColor: '#E8590C' },
  4: { label: '年卡', color: '#FFFFFF', bgColor: '#F5222D' }
};

const UserMember: React.FC = () => {
  const [filterForm] = Form.useForm();
  const [addForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<UmsUserMember[]>([]);

  const [addVisible, setAddVisible] = useState(false);
  const [addConfirmLoading, setAddConfirmLoading] = useState(false);

  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userFetching, setUserFetching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const refFetchId = useRef(0);

  const cardStatusFixed = 1; // 当前只查询：已使用

  const getList = useCallback(
    () => {
      setLoading(true);
      const values = filterForm.getFieldsValue() as {
        gameName?: string;
        phone?: string;
        oneId?: string;
        orderId?: string;
        cardType?: CardType | '' | undefined;
        openTimeRange?: (string | Date)[];
        expireTimeRange?: (string | Date)[];
        isValid?: 'true' | 'false' | '' | undefined;
      };

      const params: Record<string, any> = {
        cardStatus: cardStatusFixed,
        pageNum,
        pageSize
      };

      if (values?.gameName) params.gameName = values.gameName;
      if (values?.phone) params.phone = values.phone;
      const oneIdTrim = values?.oneId != null ? String(values.oneId).trim() : '';
      if (oneIdTrim) params.oneId = oneIdTrim;
      if (values?.orderId) params.orderId = values.orderId;
      if (values?.cardType !== undefined && values.cardType !== '') params.cardType = Number(values.cardType);
      const openTimeRange = values.openTimeRange || [];
      if (openTimeRange.length === 2) {
        params.openStartTime = dayjs(openTimeRange[0]).format('YYYY-MM-DD');
        params.openEndTime = dayjs(openTimeRange[1]).format('YYYY-MM-DD');
      }
      const expireTimeRange = values.expireTimeRange || [];
      if (expireTimeRange.length === 2) {
        params.expireStartTime = dayjs(expireTimeRange[0]).format('YYYY-MM-DD');
        params.expireEndTime = dayjs(expireTimeRange[1]).format('YYYY-MM-DD');
      }
      if (values?.isValid !== undefined && values.isValid !== '') params.isValid = values.isValid === 'true';

      get('/userMember/list', params)
        .then((res: any) => {
          const { code, data, message } = res || {};
          if (code === 200) {
            const { list = [], total: totalCount } = data || {};
            setDataList(list || []);
            setTotal(totalCount || 0);
          } else {
            Message.error(message || '获取数据失败!');
          }
        })
        .catch(() => {
          Message.error('网络错误!');
        })
        .finally(() => setLoading(false));
    },
    [filterForm, pageNum, pageSize]
  );

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  const debouncedFetchUsers = useCallback(
    debounce((keyword: string) => {
      const safeKeyword = (keyword || '').trim();
      if (!safeKeyword) {
        setUserOptions([]);
        return;
      }

      refFetchId.current = Date.now();
      const fetchId = refFetchId.current;
      setUserFetching(true);
      setUserOptions([]);

      // 这里沿用勋章管理“绑定勋章”里的查询用户逻辑：GET /backend/umsUser/list?gameName=xxx
      get('/backend/umsUser/list', { gameName: safeKeyword, pageNum: 1, pageSize: 20 })
        .then((res: any) => {
          const { code, data } = res || {};
          if (refFetchId.current !== fetchId) return;
          if (code === 200) {
            const list = data?.list || [];
            const options = list.map((item: any) => {
              return {
                label: item.gameName || '',
                value: item.openId || '',
                phone: item.phone
              };
            });
            setUserOptions(options);
          }
        })
        .catch(() => {
          // 忽略：只在用户下拉为空时不展示内容即可
        })
        .finally(() => setUserFetching(false));
    }, 500),
    []
  );

  const fetchAddOrderId = useCallback(() => {
    get('/userMember/generateOrderId')
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          addForm.setFieldValue('orderId', res.data);
        }
      })
      .catch(() => {
        // 提交时后端仍会兜底生成
      });
  }, [addForm]);

  const openAddModal = () => {
    setSelectedUser(null);
    setUserOptions([]);
    setAddVisible(true);

    addForm.resetFields();
    addForm.setFieldsValue({
      cardStatus: cardStatusFixed,
      cardType: 4, // 默认选择年卡
      orderId: ''
    });
    fetchAddOrderId();
  };

  const handleQuery = useCallback(() => {
    if (pageNum !== 1) {
      setPageNum(1);
    } else {
      getList();
    }
  }, [getList, pageNum]);

  const handleReset = useCallback(() => {
    filterForm.resetFields();

    const shouldResetPagination = pageNum !== 1 || pageSize !== 20;
    if (shouldResetPagination) {
      setPageNum(1);
      setPageSize(20);
      return;
    }

    // 已经是初始化分页，手动拉一次
    getList();
  }, [filterForm, getList, pageNum, pageSize]);

  const closeAddModal = () => {
    setAddVisible(false);
    setAddConfirmLoading(false);
    setSelectedUser(null);
    addForm.resetFields();
    setUserOptions([]);
  };

  const submitAdd = () => {
    if (!selectedUser) {
      Message.error('请选择用户');
      return;
    }
    addForm
      .validate()
      .then(values => {
        const payload = {
          openId: values.openId,
          gameName: selectedUser.label,
          phone: selectedUser.phone || values.phone,
          orderId: values.orderId || undefined,
          cardType: Number(values.cardType),
          cardStatus: cardStatusFixed,
          remark: values.remark || undefined
        };

        setAddConfirmLoading(true);
        post('/userMember/add', payload)
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('新增会员成功！');
              closeAddModal();
              getList();
            } else {
              Message.error(message || '新增会员失败！');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => setAddConfirmLoading(false));
      })
      .catch(() => {
        Message.error('请检查表单填写是否正确!');
      });
  };

  const handleDelete = (record: UmsUserMember) => {
    if (!record?.id) {
      Message.error('会员ID不存在！');
      return;
    }

    Modal.confirm({
      title: '提示',
      content: '确定删除该会员吗？',
      focusLock: true,
      onOk: () => {
        setDeleteLoading(true);
        return post(`/userMember/delete/${record.id}`, {})
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('删除成功！');
              getList();
            } else {
              Message.error(message || '删除失败！');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => setDeleteLoading(false));
      }
    });
  };

  const columns: TableColumnProps[] = useMemo(
    () => [
      {
        title: '序号',
        width: 70,
        fixed: 'left',
        render: (_text, _record, idx) => (pageNum - 1) * pageSize + idx + 1
      },
      {
        title: 'openId',
        dataIndex: 'openId',
        width: 170
      },
      {
        title: 'oneId',
        dataIndex: 'oneId',
        width: 120,
        render: text => (text ? text : '-')
      },
      {
        title: '用户名',
        dataIndex: 'gameName',
        width: 120
      },
      {
        title: '手机号',
        dataIndex: 'phone',
        width: 160,
        render: (text: string) => <PhoneCell phone={text} />
      },
      {
        title: '订单号',
        dataIndex: 'orderId',
        width: 160
      },
      {
        title: '卡号',
        dataIndex: 'cardNo',
        width: 140
      },
      {
        title: '会员卡类型',
        dataIndex: 'cardType',
        width: 140,
        render: (text: CardType) => {
          const info = cardTypeMap[text];
          return (
            <Tag
              color={info.bgColor}
              style={{ color: info.color, border: 'none' }}
            >
              {info.label}
            </Tag>
          );
        }
      },
      {
        title: '会员卡状态',
        dataIndex: 'cardStatus',
        width: 130,
        render: (text: number) => (
          <Tag color="#F2F3F5" style={{ color: '#86909C', border: '1px solid #F2F3F5' }}>
            {text === 1 ? '已使用' : '-'}
          </Tag>
        )
      },
      {
        title: '开通时间',
        dataIndex: 'openTime',
        width: 140,
        render: text => (text ? dayjs(text).format('YYYY-MM-DD') : '-')
      },
      {
        title: '到期时间',
        dataIndex: 'expireTime',
        width: 140,
        render: text => (text ? dayjs(text).format('YYYY-MM-DD') : '-')
      },
      {
        title: '是否删除',
        dataIndex: 'isValid',
        width: 130,
        render: (_text, record: UmsUserMember) => {
          const isDeleted = record.isValid === 0;
          return (
            <Tag
              color={isDeleted ? '#F2F3F5' : '#E8F3FF'}
              style={{
                color: isDeleted ? '#86909C' : '#2F6BFF',
                border: 'none'
              }}
            >
              {isDeleted ? '已删除' : '未删除'}
            </Tag>
          );
        }
      },
      {
        title: '操作',
        width: 120,
        fixed: 'right',
        render: (_text, record: UmsUserMember) =>
          record.isValid === 0 ? null : (
            <Button
              type="text"
              status="danger"
              size="small"
              loading={deleteLoading}
              onClick={() => handleDelete(record)}
            >
              删除
            </Button>
          )
      }
    ],
    [pageNum, pageSize, deleteLoading]
  );

  return (
    <div className="user-member-management table-flex">
      <Space style={{ flexWrap: 'wrap', marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Form form={filterForm} layout="inline" autoComplete="off" style={{ flexWrap: 'wrap' }}>
          <Form.Item field="gameName" style={{ marginRight: 16 }}>
            <Input style={{ width: 190 }} placeholder="请输入用户名" allowClear />
          </Form.Item>
          <Form.Item field="phone" style={{ marginRight: 16 }}>
            <Input style={{ width: 190 }} placeholder="请输入手机号" allowClear />
          </Form.Item>
          <Form.Item field="oneId" style={{ marginRight: 16 }}>
            <Input style={{ width: 190 }} placeholder="请输入 oneId" allowClear />
          </Form.Item>
          <Form.Item field="orderId" style={{ marginRight: 16 }}>
            <Input style={{ width: 190 }} placeholder="请输入订单号" allowClear />
          </Form.Item>

          <Form.Item field="cardType" style={{ marginRight: 16 }}>
            <Select style={{ width: 160 }} placeholder="会员卡类型" allowClear>
              <Option value="">
                全部
              </Option>
              <Option value={1}>周卡</Option>
              <Option value={2}>月卡</Option>
              <Option value={3}>季卡</Option>
              <Option value={4}>年卡</Option>
            </Select>
          </Form.Item>

          <Form.Item label="开通时间" field="openTimeRange" style={{ marginRight: 16 }}>
            <RangePicker style={{ width: 260 }} placeholder={['开始日期', '结束日期']} />
          </Form.Item>

          <Form.Item label="过期时间" field="expireTimeRange" style={{ marginRight: 16 }}>
            <RangePicker style={{ width: 260 }} placeholder={['开始日期', '结束日期']} />
          </Form.Item>

          <Form.Item field="isValid" style={{ marginRight: 16 }}>
            <Select style={{ width: 160 }} placeholder="是否删除" allowClear>
              <Option value="">
                全部
              </Option>
              {/* 后端：true=未删除(is_valid=1)，false=已删除(is_valid=0) */}
              <Option value="true">未删除</Option>
              <Option value="false">已删除</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" onClick={handleQuery} loading={loading}>
              查询
            </Button>
          </Form.Item>

          <Form.Item>
            <Button type="outline" onClick={handleReset} loading={loading}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Button className="filter-item" type="primary" onClick={openAddModal}>
          添加会员
        </Button>
      </Space>

      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          loading={loading}
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            sizeOptions: [20, 50, 100],
            onChange: (num: number, size?: number) => {
              setPageNum(num);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>

      {/* 添加会员弹框 */}
      <Modal
        title="添加会员"
        visible={addVisible}
        onCancel={closeAddModal}
        onOk={submitAdd}
        unmountOnExit
        style={{ width: 560 }}
        confirmLoading={addConfirmLoading}
      >
        <Form layout="vertical" form={addForm} autoComplete="off">
          {/* openId 通过 Select 选中后回填 */}
          <Form.Item
            style={{ display: 'none' }}
            field="openId"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="用户名（搜索）" required>
            <Select
              showSearch
              value={selectedUser?.value}
              placeholder="请输入用户名搜索"
              options={userOptions as any}
              filterOption={false}
              notFoundContent={
                userFetching ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin style={{ margin: 12 }} />
                  </div>
                ) : null
              }
              onSearch={debouncedFetchUsers}
              onChange={(val: string) => {
                const found = userOptions.find(u => u.value === val) || null;
                setSelectedUser(found);
                addForm.setFieldsValue({
                  openId: val,
                  gameName: found?.label || ''
                });
              }}
            />
          </Form.Item>

          <Form.Item label="用户手机号(只读)">
            <PhoneCell phone={selectedUser?.phone} />
          </Form.Item>

          <Form.Item label="openid(只读)">
            <Input value={selectedUser?.value || ''} disabled />
          </Form.Item>

          <Form.Item
            label="会员卡类型"
            field="cardType"
            rules={[{ required: true, message: '请选择会员卡类型' }]}
          >
            <Select placeholder="请选择会员卡类型">
              {(
                [
                  { value: 1, label: '周卡' },
                  { value: 2, label: '月卡' },
                  { value: 3, label: '季卡' },
                  { value: 4, label: '年卡' }
                ] as { value: CardType; label: string }[]
              ).map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="订单号" field="orderId">
            <Input readOnly placeholder="系统自动生成" />
          </Form.Item>

          <Form.Item label="备注" field="remark">
            <Input placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserMember;

